import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { Box, Button, Divider, Typography } from '@mui/material';
import DotSeparator from '@sharedCR/common/DotSeparator';
import FormGenerator from '@sharedCR/common/formGenerator/FormGenerator';
import { MoreButtonCDS } from '@sharedCR/coreDesignSystem/Buttons';
import { EditPenIcon } from '@sharedCR/coreDesignSystem/icons';
import MenuPopoverCDS from '@sharedCR/coreDesignSystem/MenuPopoverCDS';
import dayjs from 'dayjs';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { COMMON_DATETIME_FORMAT_FOR_TREE_ITEM } from 'constants/date';
import usePopover from 'hooks/usePopover';
import { formatDate } from 'utils/date';
import { getPersonFullName } from 'utils/loanApplication';

import { selectUserData } from 'store/auth/selectors';

import { LoanFeesTestIds } from '../../../../../../LoanFees.testIds';
import { getEditCICFormConfig } from '../../../AccordionDetailsEdit/FeeTable/FeeTableRow/FeeEditMenu/FeeEditMenu.config';
import { EDIT_ALLOWED_STATUSES } from '../CicFeeReasonEditItem.constants';

import {
  editFormStyles,
  getNoteStyles,
  noteContentWithMenuStyles,
  noteContentWrapperStyles,
  noteMetadataStyles,
  noteTextStyles,
  saveButtonWrapperStyles,
} from './NoteSection.styles';

const NoteSection = ({ reason, formContext, isReadOnly = false }) => {
  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();
  const actionAnchorId = anchorEl ? 'note-action-popover' : undefined;

  const currentUser = useSelector(selectUserData);

  const { loanCicFeeChangeReasonId, loanCicReasonId } = reason;

  const reasonId = loanCicFeeChangeReasonId ?? loanCicReasonId;

  const {
    textNote: formTextNote,
    deletionReason: formDeletionReason,
    modifiedBy: formModifiedBy,
    modifiedDate: formModifiedDate,
  } = formContext.form[reasonId] ?? {};

  const noteText = formDeletionReason || formTextNote;
  const modifiedBy = formModifiedBy ?? '';
  const modifiedDate = formModifiedDate ?? '';
  const isDeleted = !!formDeletionReason;

  const [isEditMode, setIsEditMode] = useState(false);
  const [defaultNote, setDefaultNote] = useState(noteText);

  const { form, validationErrors, setInForm, validateField, handleChange } = formContext || {};

  const textNotePath = `${reasonId}.textNote`;
  const modifiedByPath = `${reasonId}.modifiedBy`;
  const modifiedDatePath = `${reasonId}.modifiedDate`;

  const reasonNote = _.get(reason, 'note', '');
  const currentNoteText = _.get(form, textNotePath, noteText) || '';
  const formReasonStatus = _.get(form, `${reasonId}.reasonStatus`, reason.status);
  const isEditAllowed = !isReadOnly && EDIT_ALLOWED_STATUSES.includes(formReasonStatus);

  const isDisabledSave =
    !_.isEmpty(validationErrors?.[loanCicFeeChangeReasonId]) ||
    !currentNoteText ||
    currentNoteText.trim() === reasonNote.trim();

  const onEdit = useCallback(
    e => {
      onClearAnchorElement(e);
      setIsEditMode(true);
    },
    [onClearAnchorElement],
  );

  const onSave = useCallback(() => {
    setInForm(modifiedByPath, getPersonFullName(currentUser));
    setInForm(modifiedDatePath, dayjs().toISOString());

    setIsEditMode(false);
    setDefaultNote(currentNoteText);
  }, [currentNoteText, currentUser, modifiedByPath, modifiedDatePath, setInForm]);

  const onCancel = useCallback(() => {
    setIsEditMode(false);
    setInForm(textNotePath, defaultNote);
  }, [defaultNote, setInForm, textNotePath]);

  const menuItems = useMemo(
    () => [
      {
        text: 'Edit Note',
        onClick: onEdit,
        icon: <EditPenIcon size={IconSize[20]} />,
        'data-id': 'edit-note',
      },
    ],
    [onEdit],
  );

  return (
    <Box sx={getNoteStyles({ isDeleted })}>
      {isEditMode ? (
        <Box sx={editFormStyles}>
          <FormGenerator
            isNestedForm
            config={getEditCICFormConfig({ loanCicFeeChangeReasonId: reasonId })}
            form={form}
            handleChange={handleChange}
            numberOfColumns={1}
            setInForm={setInForm}
            validateField={validateField}
            validationErrors={validationErrors}
          />
          <Box sx={saveButtonWrapperStyles}>
            <Button
              data-testid={LoanFeesTestIds.NOTE_CANCEL_BUTTON}
              size="small"
              variant="outlined"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              data-testid={LoanFeesTestIds.NOTE_SAVE_BUTTON}
              disabled={isDisabledSave}
              size="small"
              onClick={onSave}
            >
              Save
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={noteContentWithMenuStyles}>
          <Box sx={noteContentWrapperStyles}>
            <Typography color="text.secondary" sx={noteTextStyles} variant="regular14">
              {noteText}
            </Typography>
          </Box>
          {isEditAllowed && (
            <>
              <MoreButtonCDS
                aria-describedby={actionAnchorId}
                isSelected={!!anchorEl}
                onClick={onSetAnchorElement}
              />
              <MenuPopoverCDS
                anchorEl={anchorEl}
                id={actionAnchorId}
                items={menuItems}
                onClose={onClearAnchorElement}
              />
            </>
          )}
        </Box>
      )}

      <Divider />

      <Typography color="text.secondary" sx={noteMetadataStyles} variant="regular14">
        <span>Updated: {formatDate(modifiedDate, COMMON_DATETIME_FORMAT_FOR_TREE_ITEM)}</span>
        <DotSeparator />
        <span>By: {modifiedBy}</span>
      </Typography>
    </Box>
  );
};

NoteSection.propTypes = {
  reason: PropTypes.object.isRequired,
  formContext: PropTypes.object.isRequired,
  isReadOnly: PropTypes.bool,
};

export default NoteSection;
